import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence } from 'framer-motion'
import { cn, DropdownChevron, DropdownPanel } from '@workflow/ui'
import { monthName } from './jobPeriods'

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const ROW = 40
const SHOWN = 5

/**
 * The month's name with a small chevron; pressed, it opens the picker a phone shows —
 * a wheel of months beside a wheel of years, turned by scrolling or by a
 * press, the choice sitting in the band across the middle.
 */
export function MonthSwitcher({ value, onChange }: { value: string; onChange: (month: string) => void }) {
  const [open, setOpen] = useState(false)
  const [place, setPlace] = useState<{ left: number; top: number } | null>(null)
  const trigger = useRef<HTMLDivElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const year = Number(value.slice(0, 4))
  const month = Number(value.slice(5, 7)) - 1
  const now = new Date().getFullYear()
  const years = Array.from({ length: now - 2020 + 2 }, (_, index) => 2020 + index)

  useLayoutEffect(() => {
    if (!open || !trigger.current) return
    const box = trigger.current.getBoundingClientRect()
    setPlace({ left: Math.max(8, Math.min(box.right - 288, window.innerWidth - 296)), top: box.bottom + 8 })
  }, [open])

  useEffect(() => {
    if (!open) return
    const close = (event: Event) => {
      if (event.type === 'keydown' && (event as KeyboardEvent).key !== 'Escape') return
      if (event.type === 'mousedown' && (panel.current?.contains(event.target as Node) || trigger.current?.contains(event.target as Node))) return
      setOpen(false)
    }
    const shut = () => setOpen(false)
    window.addEventListener('mousedown', close)
    window.addEventListener('keydown', close)
    window.addEventListener('resize', shut)
    return () => {
      window.removeEventListener('mousedown', close)
      window.removeEventListener('keydown', close)
      window.removeEventListener('resize', shut)
    }
  }, [open])

  const pick = (nextYear: number, nextMonth: number) => onChange(`${nextYear}-${String(nextMonth + 1).padStart(2, '0')}`)

  return (
    <>
      {/* Words, not a box: the month reads like the page's own text and opens on a press. */}
      <div ref={trigger}>
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
          className="inline-flex h-8 items-center gap-1.5 rounded-full px-2.5 text-ui font-medium text-foreground outline-none transition-colors duration-fast hover:bg-tint focus-visible:ring-1 focus-visible:ring-foreground"
        >
          {monthName(value)}
          <DropdownChevron size="sm" open={open} />
        </button>
      </div>
      {createPortal(
        <AnimatePresence>
          {open && place ? (
            <DropdownPanel
              ref={panel}
              role="dialog"
              aria-label="Choose a month"
              style={{ left: place.left, top: place.top }}
              className="fixed z-[200] w-[288px] rounded-3xl bg-surface px-4 py-3 shadow-overlay"
            >
              <div className="relative grid grid-cols-[1fr_6rem] gap-2">
                {/* Two hairlines mark where the choice rests, shared by both wheels. */}
                <div aria-hidden className="pointer-events-none absolute inset-x-0 border-y border-hairline" style={{ top: ROW * 2, height: ROW }} />
                <Wheel label="Month" items={MONTHS} index={month} onPick={(index) => pick(year, index)} />
                <Wheel label="Year" items={years.map(String)} index={Math.max(0, years.indexOf(year))} onPick={(index) => pick(years[index]!, month)} />
              </div>
            </DropdownPanel>
          ) : null}
        </AnimatePresence>,
        document.body,
      )}
    </>
  )
}

/** One turning column: it snaps a row into the middle band and reports which, once it comes to rest. */
function Wheel({ label, items, index, onPick }: { label: string; items: string[]; index: number; onPick: (index: number) => void }) {
  const list = useRef<HTMLDivElement>(null)
  const settle = useRef<number | null>(null)
  const [centre, setCentre] = useState(index)

  useLayoutEffect(() => {
    if (list.current) list.current.scrollTop = index * ROW
    setCentre(index)
  }, [index])
  useEffect(() => () => { if (settle.current !== null) window.clearTimeout(settle.current) }, [])

  return (
    <div
      ref={list}
      role="listbox"
      aria-label={label}
      onScroll={(event) => {
        const at = Math.max(0, Math.min(items.length - 1, Math.round(event.currentTarget.scrollTop / ROW)))
        setCentre(at)
        if (settle.current !== null) window.clearTimeout(settle.current)
        settle.current = window.setTimeout(() => { if (at !== index) onPick(at) }, 140)
      }}
      className="relative snap-y snap-mandatory overflow-y-auto"
      style={{
        height: ROW * SHOWN,
        // Inline, so the app-wide thin rail rule cannot win: a wheel shows no rail at all.
        scrollbarWidth: 'none',
        paddingBlock: ROW * 2,
        maskImage: 'linear-gradient(to bottom, transparent, black 30%, black 70%, transparent)',
      }}
    >
      {items.map((item, position) => (
        <button
          key={item}
          type="button"
          role="option"
          aria-selected={position === index}
          onClick={() => list.current?.scrollTo({ top: position * ROW, behavior: 'smooth' })}
          className={cn(
            'block w-full snap-center text-center outline-none transition-[color,font-size] duration-fast',
            position === centre ? 'text-[15px] font-medium text-foreground' : 'text-ui text-muted-foreground',
          )}
          style={{ height: ROW }}
        >
          {item}
        </button>
      ))}
    </div>
  )
}
